
import { firstValueFrom, of as observableOf } from 'rxjs'

import { createAction } from '@reduxjs/toolkit'
import { } from 'interfaces'
import { catchError, take } from 'rxjs/operators'
import { currentUserSelector } from 'services/users/selectors'
import { PostDestination, PostDestinationType, destinationsAdapter } from 'shared'
import StoreState, { createAppAsyncThunk } from 'store/state'
import { api } from './net'
import { userDestinationsSelector } from './selectors'

export const getDestinationsSuccess = createAction<PostDestination[]>('destinations/getSuccess')

export const getDestinations = createAppAsyncThunk(
  'profiles/get',
  async (force: boolean = false, { dispatch, getState }) => {
    if (!force) {
      const cached = userDestinationsSelector(getState() as StoreState)
      if (Object.keys(cached).length > 0) {
        const destinations = Object.values(cached)
        dispatch(getDestinationsSuccess(destinations))
        return { destinations }
      }
    }

    const response = await firstValueFrom(dispatch(api.getDestinations()))
    const destinations = destinationsAdapter(response.postDestinations)
    // NOTE: prevent setting profiles after logout
    const isLoggedIn = !!currentUserSelector(getState() as StoreState)?.accessToken
    if (isLoggedIn) {
      dispatch(getDestinationsSuccess(destinations))
    }

    return {
      fbPersonalProfiles: isLoggedIn ? destinationsAdapter(response.facebookProfiles) : [],
      destinations: isLoggedIn ? destinations : []
    }
  }
)

export const getAlbums = createAppAsyncThunk(
  'profiles/getAlbums',
  async (profile: PostDestination, { dispatch }) => {
    const response = await firstValueFrom(dispatch(api.getAlbums(profile.ppid)))
    return {
      profileId: profile.id,
      albums: response.albums.reduce((map: any, album: any) => {
        map[album.id] = { ...album, ppid: profile.ppid }
        return map
      }, {})
    }
  }
)

export const getPinterestBoards = createAppAsyncThunk(
  'profiles/getPinterestBoards',
  async (args: { profile: PostDestination, noCache?: boolean }, { dispatch, rejectWithValue }) => {
    const { profile, noCache } = args
    try {
      const response = await firstValueFrom(dispatch(api.getAlbums(profile.ppid, noCache)))
      // NOTE: the legacy endpoint answers 200 with an `error` key when Pinterest rejects the request
      if (response?.error) {
        return rejectWithValue(response.error)
      }
      return {
        profileId: profile.id,
        ppid: profile.ppid,
        boards: response.albums.reduce((map: any, album: any) => {
          map[album.id] = {
            ...album,
            url: `https://pinterest.com${album.url}`,
            ppid: profile.ppid
          }
          return map
        }, {})
      }
    } catch (e) {
      return rejectWithValue(e)
    }
  }
)

export const toggleProfileConnected = createAppAsyncThunk(
  'profiles/toggleConnected',
  async (
    args: { id: string, ppid: string, type: PostDestinationType, forceDeletePosts?: boolean, isIgDirect?: boolean },
    { dispatch, rejectWithValue }
  ) => {
    const response = await firstValueFrom(
      dispatch(api.toggleConnected(args.id, args.ppid, args.type, args.forceDeletePosts, args.isIgDirect)
      )
        .pipe(catchError(error => {
          return observableOf({ error })
        })).pipe(
          take(1)))

    if ((response as any).error) {
      return rejectWithValue((response as any).error)
    }
    return args.id
  }
)

export const refreshMyProfiles = createAppAsyncThunk(
  'profiles/refreshMyProfiles',
  async (_, { dispatch }) => {
    await firstValueFrom(dispatch(api.refreshMyProfiles()))
  }
)

export const removeProfile = createAppAsyncThunk(
  'profiles/remove',
  async (args: { id: string, ppid: string, forceDelete: boolean }, { dispatch, rejectWithValue }) => {
    const res = await firstValueFrom(dispatch(api.removeProfile(args.ppid, args.forceDelete))
      .pipe(catchError(error => {
        return observableOf({ error })
      })).pipe(
        take(1)))

    if (res.error) {
      return rejectWithValue(res.error)
    }
    return args.id
  }
)

export const connectBluesky = createAppAsyncThunk(
  'profiles/connectBluesky',
  async (args: { username: string, password: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await firstValueFrom(dispatch(api.connectBlueskyAccount(args.username, args.password)))
      return response
    } catch (e) {
      console.error(e)
      return rejectWithValue(e)
    }
  }
)
